/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-empty-function */

"use client";

import { createContext, useEffect, useState, ReactNode, useMemo } from "react";
import {
  CartInterface,
  CheckoutInterface,
  ProductCartVariantInterface,
} from "@/types/cart";
import { isBothCartVariantSame } from "@/utils/formate-product-variant";
import { useQuery } from "@tanstack/react-query";
import { settingsService } from "@/services/settings";

export enum SettingKeys {
  shipping_fee = "shipping_fee",
  min_order_amount = "min_order_amount",
}

const initialCheckout: CheckoutInterface = {
  discountPrice: 0,
  price: 0,
  total: 0,
  shipping: 0,
};

export interface CartContextProps {
  cartData: CartInterface[];
  checkoutData: CheckoutInterface;
  addToCart: (newProduct: CartInterface) => void;
  incQuantity: (
    id: number,
    variants: ProductCartVariantInterface[],
    quantity: number,
  ) => void;
  decQuantity: (
    id: number,
    variants: ProductCartVariantInterface[],
    quantity: number,
  ) => void;
  removeCartData: (id: number, variants: ProductCartVariantInterface[]) => void;
  setCartData: (data: CartInterface[]) => void;
  updateCartDataLocalStorage: (data: CartInterface[]) => void;
}

const initialValues: CartContextProps = {
  cartData: [],
  checkoutData: initialCheckout,
  addToCart: () => {},
  incQuantity: () => {},
  decQuantity: () => {},
  removeCartData: () => {},
  setCartData: () => {},
  updateCartDataLocalStorage: () => {},
};

export const CartContext = createContext<CartContextProps>(initialValues);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartData, setCartData] = useState<CartInterface[]>([]);
  const [checkoutData, setCheckoutData] =
    useState<CheckoutInterface>(initialCheckout);

  const updateCartDataLocalStorage = (data: CartInterface[]) => {
    localStorage.setItem("Cart", JSON.stringify(data));
    window.dispatchEvent(new Event("storage"));
  };

  const { data: shippingSettings } = useQuery([SettingKeys.shipping_fee], () =>
    settingsService.get(SettingKeys.shipping_fee),
  );
  const { data: minOrderAmount } = useQuery(
    [SettingKeys.min_order_amount],
    () => settingsService.get(SettingKeys.min_order_amount),
  );

  const shippingValue = useMemo(() => {
    if (shippingSettings?.data) {
      return Number(shippingSettings.data.value);
    }
    return 0;
  }, [shippingSettings?.data]);

  const minOrderValue = useMemo(() => {
    if (minOrderAmount?.data) {
      return Number(minOrderAmount.data.value);
    }
    return 0;
  }, [minOrderAmount?.data]);

  console.log(shippingSettings, "shippingFee", minOrderAmount);

  const addToCart = (newProduct: CartInterface) => {
    const { slug, id, variants } = newProduct;
    setCartData((prev) => {
      const duplicateProductIndex = prev.findIndex(
        (item) =>
          item.slug === slug &&
          item.id === id &&
          isBothCartVariantSame(item.variants, variants),
      );

      if (duplicateProductIndex >= 0) {
        const productVariant = prev[duplicateProductIndex].variants;
        const newVariant = newProduct.variants;

        const isSame = isBothCartVariantSame(productVariant, newVariant);

        if (!isSame) {
          const updatedData = [...prev, { ...newProduct }];
          updateCartDataLocalStorage(updatedData);
          return updatedData;
        }

        return prev;
      }

      if (!variants) {
        newProduct.variants = [];
      }

      const updatedData = [...prev, { ...newProduct }];
      updateCartDataLocalStorage(updatedData);
      return updatedData;
    });
  };

  const incQuantity = (
    id: number,
    variants: ProductCartVariantInterface[],
    quantity: number,
  ) => {
    setCartData((prev) => {
      const updatedData = prev.map((item) => {
        if (item.id === id && isBothCartVariantSame(item.variants, variants)) {
          const newItem = { ...item };
          newItem.quantity = quantity;
          return newItem;
        }
        return item;
      });
      updateCartDataLocalStorage(updatedData);
      return updatedData;
    });
  };

  const decQuantity = (
    id: number,
    variants: ProductCartVariantInterface[],
    quantity: number,
  ) => {
    setCartData((prev) => {
      const updatedData = prev.map((item) => {
        if (item.id === id && isBothCartVariantSame(item.variants, variants)) {
          const newItem = { ...item };
          if (quantity > 0) {
            newItem.quantity = quantity;
          }
          return newItem;
        }
        return item;
      });
      updateCartDataLocalStorage(updatedData);
      return updatedData;
    });
  };

  const removeCartData = (
    id: number,
    variants: ProductCartVariantInterface[],
  ) => {
    setCartData((prev) => {
      const updatedData = prev.filter(
        (item) =>
          !(item.id === id && isBothCartVariantSame(item.variants, variants)),
      );

      updateCartDataLocalStorage(updatedData);
      return updatedData;
    });
  };

  const calculateTotalPrice = () =>
    cartData.reduce((acc, { quantity, price }) => acc + quantity * price, 0);

  const calculateTotalDiscount = () =>
    cartData.reduce(
      (acc, { quantity, flatDiscount }) => acc + quantity * (flatDiscount || 0),
      0,
    );

  const calculateTotalShipping = () =>
    cartData.reduce(
      (acc, { quantity, shippingFee }) => acc + quantity * shippingFee,
      0,
    );

  const checkLocalStorage = () => {
    setCartData(() => {
      const storedCartData = localStorage.getItem("Cart");
      const parsedCartData: CartInterface[] = storedCartData
        ? JSON.parse(storedCartData)
        : [];

      return parsedCartData;
    });
  };

  const handleStorageChange = () => {
    checkLocalStorage();
  };

  useEffect(() => {
    checkLocalStorage();

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    setCheckoutData(() => {
      const price = calculateTotalPrice();
      const discountPrice = calculateTotalDiscount();
      const productShipping = calculateTotalShipping();
      const shipping =
        price - discountPrice < minOrderValue
          ? shippingValue + productShipping
          : productShipping;

      return {
        price,
        discountPrice,
        shipping,
        total: price - discountPrice + shipping,
      };
    });
  }, [cartData]);

  const value = useMemo(
    () => ({
      cartData,
      checkoutData,
      addToCart,
      incQuantity,
      decQuantity,
      removeCartData,
      setCartData,
      updateCartDataLocalStorage,
    }),
    [cartData, checkoutData],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
